// Launch Media (SM-1): the account's library on the REST API (/v1/mediaItems). Every route is
// scoped to the signed-in account on the server.
import { API_DOMAIN } from "@launchos/shared-utils/env";
import type { MediaCounts, MediaFile } from "@/Apps/Media/library";

const call = async <T,>(token: string, path: string, init: RequestInit = {}): Promise<T> => {
  const res = await fetch(`${API_DOMAIN}/mediaItems${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.body ? { "Content-Type": "application/json" } : {}), ...(init.headers || {}) },
  });
  let data: any = null;
  try { data = await res.json(); } catch (e) { /* no body */ }
  if (!res.ok) throw new Error((data && data.error) || "Something went wrong. Try again.");
  return data as T;
};

export const listMedia = (token: string) => call<{ files: MediaFile[]; counts: MediaCounts }>(token, "");
export const renameMedia = (token: string, id: string, name: string) => call<MediaFile>(token, `/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name }) });
export const deleteMedia = (token: string, id: string) => call<{ deleted: string }>(token, `/${encodeURIComponent(id)}`, { method: "DELETE" });

// SM-2: uploads go straight to the private bucket
export const startUpload = (token: string, f: { name: string; contentType: string; size: number }) =>
  call<{ key: string; uploadUrl: string }>(token, "/uploads", { method: "POST", body: JSON.stringify(f) });
export const finishUpload = (token: string, key: string, name: string, campaignId?: string | null) =>
  call<MediaFile>(token, "/uploads/complete", { method: "POST", body: JSON.stringify({ key, name, ...(campaignId ? { campaignId } : {}) }) });
// sends the file to its one-time link with progress (fetch can't report upload progress). The headers
// must match what the link was signed with.
export const sendToStorage = (uploadUrl: string, file: File, onPct: (pct: number) => void, contentType: string) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.setRequestHeader("x-amz-server-side-encryption", "AES256");
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onPct(Math.round((100 * e.loaded) / e.total)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? (onPct(100), resolve()) : reject(new Error("The upload was refused. Try again.")));
    xhr.onerror = () => reject(new Error("The upload didn't go through. Check your connection and try again."));
    xhr.send(file);
  });

// SM-3/SM-4: a short-lived link to view a file (download: save it; days: share it, up to 7)
export const mediaLink = (token: string, id: string, { download = false, days = 0 }: { download?: boolean; days?: number } = {}) =>
  call<{ url: string; seconds?: number }>(token, `/${encodeURIComponent(id)}/link${download || days ? `?${download ? "download=1" : ""}${download && days ? "&" : ""}${days ? `days=${days}` : ""}` : ""}`);

// SM-10: make a file public (a lasting link anyone can open) or private again
export const setPublic = (token: string, id: string, on: boolean) =>
  call<MediaFile>(token, `/${encodeURIComponent(id)}/public`, { method: "POST", body: JSON.stringify({ on }) });
// Kyle, Oct 30: play.launchos.com/f/<id> passes the visit to the API's public address (links made
// before still work); API_DOMAIN stays imported for the other calls
export const publicUrlOf = (publicId: string) => `https://play.launchos.com/f/${publicId}`;

// VH-4: one file (the tab checks on a video being prepared), Try again, and the player's addresses
export const getMedia = (token: string, id: string) => call<MediaFile>(token, `/${encodeURIComponent(id)}`);
export const retryVideo = (token: string, id: string) => call<MediaFile>(token, `/${encodeURIComponent(id)}/video/retry`, { method: "POST" });
// VH-5: a public video's player settings
export type PlayerSettings = { color: string; playButton: boolean; controls: boolean; autoplay: boolean; loop: boolean; poster: "frame" | "custom"; title?: string };
export const playerSettings = (token: string, id: string) => call<PlayerSettings>(token, `/${encodeURIComponent(id)}/player`);
export const savePlayerSettings = (token: string, id: string, change: Partial<PlayerSettings> & { posterFileId?: string }) =>
  call<PlayerSettings>(token, `/${encodeURIComponent(id)}/player`, { method: "POST", body: JSON.stringify(change) });
const PLAY_DOMAIN = "https://play.launchos.com";
export const playUrlOf = (publicId: string) => `${PLAY_DOMAIN}/${publicId}`;
// fills the width it's pasted into and keeps a 16:9 shape
export const embedCodeOf = (publicId: string) =>
  `<iframe src="${playUrlOf(publicId)}" style="width:100%;aspect-ratio:16/9;border:0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Video"></iframe>`;

// OD-3: a public file's short link on one of the account's domains (the account's own address first)
export const linkHosts = (token: string) => call<{ hosts: string[] }>(token, "/link-hosts");
export const setNamedLink = (token: string, id: string, link: { host: string; name: string }) =>
  call<MediaFile & { note?: string }>(token, `/${encodeURIComponent(id)}/named-link`, { method: "POST", body: JSON.stringify(link) });
export const removeNamedLink = (token: string, id: string) => call<MediaFile>(token, `/${encodeURIComponent(id)}/named-link`, { method: "DELETE" });

// SM-5: tags
export type MediaTag = { name: string; count: number };
export const listTags = (token: string) => call<MediaTag[]>(token, "/tags");
export const createTag = (token: string, name: string) => call<{ name: string }>(token, "/tags", { method: "POST", body: JSON.stringify({ name }) });
export const renameTag = (token: string, from: string, to: string) => call<{ name: string }>(token, "/tags/rename", { method: "POST", body: JSON.stringify({ from, to }) });
export const deleteTag = (token: string, name: string) => call<{ deleted: string }>(token, "/tags/delete", { method: "POST", body: JSON.stringify({ name }) });
export const tagFile = (token: string, id: string, change: { add: string } | { remove: string }) =>
  call<MediaFile>(token, `/${encodeURIComponent(id)}/tags`, { method: "POST", body: JSON.stringify(change) });

// SM-6: file under one of the account's campaigns, or take it out
export const fileUnder = (token: string, id: string, change: { add: string } | { remove: string }) =>
  call<MediaFile>(token, `/${encodeURIComponent(id)}/campaigns`, { method: "POST", body: JSON.stringify(change) });
